/**
 * Pure helpers for the CCM Wave 2 org/case workspace-scope branch shared
 * by the workspace data hooks (`useCaseWorkspaceTree`, `useCaseFolderMap`,
 * `useFileBytes`).
 *
 * The runtime gateway-methods accept `{scope:"org"}` (omitting
 * clientId/matterId/caseId; owner = LEX_USER_ID) for the per-user
 * "Global" workspace (R3, lexruntime `9ed2d9e7`). When the effective
 * scope is global (the active-context selection is null), the hooks fetch
 * the org variant; otherwise the existing case path. These helpers
 * centralise the two load-bearing decisions so they can't drift between
 * the three hooks AND so they're unit-testable in the project's node env:
 *
 *   1. The React-Query cache key must be SCOPE-DISTINCT — `"org"` vs the
 *      caseId — so a case ↔ org switch never serves the wrong scope's
 *      cached payload (e.g. a same-named `sources/x.pdf` in both scopes).
 *   2. The `enabled` gate: org needs only a live connection (no ids);
 *      case needs the full client/matter/case chain present.
 *
 * See: ai-docs/handoffs/ccm-lexide-org-pane-handoff-2026-06-28.md §3.
 */

import type { EffectiveScope } from "@/stores/useActiveContextStore";

/**
 * The scope-distinct cache-key segment: the literal `"org"` in org
 * scope, otherwise the caseId (which may be undefined when a case scope
 * is partially selected — the `enabled` gate keeps the query off in that
 * case, but the key stays stable + distinct from "org").
 */
export function scopeKeySegment(
  scope: EffectiveScope,
  caseId: string | undefined,
): string | undefined {
  return scope === "org" ? "org" : caseId;
}

/**
 * Whether the workspace query may run for the given scope + id
 * availability. Org: connection alone suffices (no ids). Case: requires
 * only the `caseId` — a flat/unassigned case (D-8) resolves its on-disk
 * location by caseId alone via the runtime's `resolveGatewayWorkspace`, so a
 * null `clientId`/`matterId` must NOT gate the query off (else an unassigned
 * case opens into a hollow workspace). The caller AND's this with its own
 * connection / path / rpc presence checks and passes any known parents through.
 */
export function workspaceQueryEnabled(
  scope: EffectiveScope,
  ids: {
    clientId: string | undefined;
    matterId: string | undefined;
    caseId: string | undefined;
  },
): boolean {
  if (scope === "org") return true;
  return Boolean(ids.caseId);
}
